// src/components/settings/PortalFormsEditor.jsx
// Team-Lead-only editor for the client-portal forms. Reads the base forms from
// PORTAL_FORMS and the current admin overrides from portalConfig/formOverrides,
// lets the Team Lead rename labels, toggle required, hide/show fields, edit
// select options, add new fields, reorder, and rename the form title — all
// without a developer. Edits produce a FormOverride per the portalForms.js
// contract and are written back with savePortalFormOverrides.
import { useEffect, useMemo, useState } from "react";
import {
  Loader2,
  Save,
  Eye,
  EyeOff,
  ChevronUp,
  ChevronDown,
  Plus,
  X,
  RotateCcw,
  Trash2,
} from "lucide-react";
import { toast } from "react-toastify";
import { PORTAL_FORMS } from "../../utils/portalForms";
import {
  loadPortalFormOverrides,
  savePortalFormOverrides,
} from "../../utils/portalFormOverrides";

// Human labels for the field types.
const TYPE_LABELS = {
  shortText: "Short text",
  longText: "Long text",
  single: "Choose one",
  multi: "Choose many",
  repeatable: "Add-more list",
  file: "File upload",
  video: "Video",
};

// Types the Team Lead can add. (file/video are code-only.)
const ADDABLE_TYPES = [
  { value: "shortText", label: "Short text" },
  { value: "longText", label: "Long text" },
  { value: "single", label: "Choose one" },
  { value: "multi", label: "Choose many" },
];

const isSelectType = (t) => t === "single" || t === "multi";

// --- pure override helpers --------------------------------------------------

function slugify(label) {
  const words = String(label || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  const s = words
    .map((w, i) => (i === 0 ? w : w.charAt(0).toUpperCase() + w.slice(1)))
    .join("");
  return s || "field";
}

function uniqueKey(base, taken) {
  let key = base;
  let n = 2;
  while (taken.has(key)) key = `${base}${n++}`;
  return key;
}

// Does this override actually carry a change? (mirrors portalForms.js)
function overrideHasChange(ov) {
  if (!ov) return false;
  return (
    ov.title != null ||
    (ov.fields && Object.keys(ov.fields).length > 0) ||
    (Array.isArray(ov.added) && ov.added.length > 0) ||
    (Array.isArray(ov.order) && ov.order.length > 0)
  );
}

// Strip empty field entries / empty forms so we never persist dead weight.
function cleanOverridesMap(map) {
  const out = {};
  for (const [formId, ovRaw] of Object.entries(map || {})) {
    const ov = { ...(ovRaw || {}) };
    if (ov.fields) {
      const fields = {};
      for (const [k, fo] of Object.entries(ov.fields)) {
        if (fo && Object.keys(fo).length > 0) fields[k] = fo;
      }
      if (Object.keys(fields).length) ov.fields = fields;
      else delete ov.fields;
    }
    if (Array.isArray(ov.added) && ov.added.length === 0) delete ov.added;
    if (Array.isArray(ov.order) && ov.order.length === 0) delete ov.order;
    if (ov.title == null) delete ov.title;
    if (overrideHasChange(ov)) out[formId] = ov;
  }
  return out;
}

// Build the editor rows for a form = base fields (carrying their override) then
// added fields, reordered by override.order (unlisted keep relative order).
function rowsFor(form, ov) {
  const override = ov || {};
  const fieldOv = override.fields || {};
  const baseRows = form.fields.map((f) => {
    const o = fieldOv[f.key] || {};
    return {
      key: f.key,
      base: true,
      type: f.type,
      baseLabel: f.label,
      label: o.label != null ? o.label : f.label,
      required: o.required != null ? o.required : !!f.required,
      hidden: !!o.hidden,
      options: o.options != null ? o.options : f.options,
      group: f.group,
      hasOverride: Object.keys(o).length > 0,
    };
  });
  const addedRows = (override.added || []).map((a) => ({
    key: a.key,
    base: false,
    type: a.type,
    label: a.label,
    required: false,
    hidden: false,
    options: a.options,
    group: a.group,
  }));
  let rows = [...baseRows, ...addedRows];
  if (Array.isArray(override.order) && override.order.length) {
    const idx = new Map(override.order.map((k, i) => [k, i]));
    rows = rows
      .map((r, i) => ({ r, i }))
      .sort((a, b) => {
        const ai = idx.has(a.r.key) ? idx.get(a.r.key) : Infinity;
        const bi = idx.has(b.r.key) ? idx.get(b.r.key) : Infinity;
        return ai !== bi ? ai - bi : a.i - b.i;
      })
      .map((x) => x.r);
  }
  return rows;
}

// --- component --------------------------------------------------------------

export default function PortalFormsEditor() {
  const [overrides, setOverrides] = useState({});
  const [savedJson, setSavedJson] = useState("{}");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedId, setSelectedId] = useState(PORTAL_FORMS[0]?.id || "");

  // add-field panel state
  const [newLabel, setNewLabel] = useState("");
  const [newType, setNewType] = useState("shortText");
  const [newOptions, setNewOptions] = useState([""]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const forms = await loadPortalFormOverrides();
      if (cancelled) return;
      const cleaned = cleanOverridesMap(forms);
      setOverrides(cleaned);
      setSavedJson(JSON.stringify(cleaned));
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const form = useMemo(
    () => PORTAL_FORMS.find((f) => f.id === selectedId) || PORTAL_FORMS[0],
    [selectedId],
  );
  const ov = useMemo(() => overrides[form?.id] || {}, [overrides, form]);
  const rows = useMemo(() => (form ? rowsFor(form, ov) : []), [form, ov]);

  const dirty = JSON.stringify(cleanOverridesMap(overrides)) !== savedJson;

  // -- mutation helpers ------------------------------------------------------

  const patchForm = (updater) =>
    setOverrides((prev) => {
      const cur = prev[form.id] || {};
      return { ...prev, [form.id]: updater(cur) };
    });

  const patchBaseField = (key, patch) =>
    patchForm((cur) => {
      const fields = { ...(cur.fields || {}) };
      const fo = { ...(fields[key] || {}), ...patch };
      Object.keys(fo).forEach((k) => fo[k] === undefined && delete fo[k]);
      if (Object.keys(fo).length === 0) delete fields[key];
      else fields[key] = fo;
      return { ...cur, fields };
    });

  const resetBaseField = (key) =>
    patchForm((cur) => {
      const fields = { ...(cur.fields || {}) };
      delete fields[key];
      return { ...cur, fields };
    });

  const patchAddedField = (key, patch) =>
    patchForm((cur) => ({
      ...cur,
      added: (cur.added || []).map((a) =>
        a.key === key ? { ...a, ...patch } : a,
      ),
    }));

  const removeAddedField = (key) =>
    patchForm((cur) => ({
      ...cur,
      added: (cur.added || []).filter((a) => a.key !== key),
      order: Array.isArray(cur.order)
        ? cur.order.filter((k) => k !== key)
        : cur.order,
    }));

  const setTitle = (value) =>
    patchForm((cur) => {
      const next = { ...cur };
      if (value == null || value === form.title) delete next.title;
      else next.title = value;
      return next;
    });

  // Reorder: write the full current key order after moving `key` by `delta`.
  const moveRow = (index, delta) => {
    const target = index + delta;
    if (target < 0 || target >= rows.length) return;
    const keys = rows.map((r) => r.key);
    const [k] = keys.splice(index, 1);
    keys.splice(target, 0, k);
    patchForm((cur) => ({ ...cur, order: keys }));
  };

  // -- option editing (base or added) ---------------------------------------

  const currentOptions = (row) =>
    Array.isArray(row.options) ? row.options : [];

  const setRowOptions = (row, options) => {
    if (row.base) patchBaseField(row.key, { options });
    else patchAddedField(row.key, { options });
  };

  const addOption = (row) => setRowOptions(row, [...currentOptions(row), ""]);

  const renameOption = (row, i, value) => {
    const opts = currentOptions(row).slice();
    opts[i] = value;
    setRowOptions(row, opts);
  };

  const removeOption = (row, i) => {
    const opts = currentOptions(row).slice();
    if (opts.length <= 1) return; // guard: never empty a select
    opts.splice(i, 1);
    setRowOptions(row, opts);
  };

  // -- add a new field -------------------------------------------------------

  const takenKeys = useMemo(() => {
    const s = new Set(form ? form.fields.map((f) => f.key) : []);
    (ov.added || []).forEach((a) => s.add(a.key));
    return s;
  }, [form, ov]);

  const addField = () => {
    const label = newLabel.trim();
    if (!label) {
      toast.error("Give the new field a label first.");
      return;
    }
    let options;
    if (isSelectType(newType)) {
      options = newOptions.map((o) => o.trim()).filter(Boolean);
      if (options.length === 0) {
        toast.error("A choice field needs at least one option.");
        return;
      }
    }
    const key = uniqueKey(slugify(label), takenKeys);
    const field = { key, label, type: newType };
    if (options) field.options = options;
    patchForm((cur) => ({ ...cur, added: [...(cur.added || []), field] }));
    setNewLabel("");
    setNewType("shortText");
    setNewOptions([""]);
  };

  // -- save ------------------------------------------------------------------

  const onSave = async () => {
    // Validate: no empty option lists on any select field.
    for (const f of PORTAL_FORMS) {
      const o = overrides[f.id];
      if (!o) continue;
      for (const [k, fo] of Object.entries(o.fields || {})) {
        if (
          fo.options != null &&
          (!Array.isArray(fo.options) ||
            fo.options.filter((x) => String(x).trim()).length === 0)
        ) {
          toast.error(`"${f.title}" has a choice field with no options.`);
          return;
        }
        void k;
      }
      for (const a of o.added || []) {
        if (isSelectType(a.type)) {
          const opts = (a.options || []).filter((x) => String(x).trim());
          if (opts.length === 0) {
            toast.error(`"${f.title}" has a new choice field with no options.`);
            return;
          }
        }
      }
    }
    setSaving(true);
    try {
      const cleaned = cleanOverridesMap(overrides);
      await savePortalFormOverrides(cleaned);
      setOverrides(cleaned);
      setSavedJson(JSON.stringify(cleaned));
      toast.success("Portal forms saved");
    } catch (err) {
      console.error("savePortalFormOverrides failed:", err);
      toast.error("Could not save. Try again.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-slate-500 text-sm p-4">
        <Loader2 size={16} className="animate-spin" />
        Loading portal forms…
      </div>
    );
  }

  const formHasOverride = overrideHasChange(cleanOverridesMap(overrides)[form?.id]);

  return (
    <div className="max-w-5xl">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">Portal Forms</h3>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl">
            Edit the questions clients see in their portal. You can rename labels,
            mark fields required, hide fields, edit choices, add new fields, and
            reorder — no developer needed.
          </p>
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-1.5 mt-2 inline-block">
            Changes apply to new and in-progress client portals.
          </p>
        </div>
        <button
          onClick={onSave}
          disabled={saving || !dirty}
          className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-md bg-themeColor hover:bg-themeColor/90 text-white disabled:opacity-50 self-start"
        >
          {saving ? (
            <Loader2 size={14} className="animate-spin" />
          ) : (
            <Save size={14} />
          )}
          {saving ? "Saving…" : dirty ? "Save changes" : "Saved"}
        </button>
      </div>

      <div className="flex flex-col lg:flex-row gap-4">
        {/* Left: form list */}
        <div className="lg:w-56 flex-shrink-0">
          <ul className="flex flex-row lg:flex-col gap-1 overflow-x-auto lg:overflow-visible">
            {PORTAL_FORMS.map((f) => {
              const active = f.id === form.id;
              const changed = overrideHasChange(
                cleanOverridesMap(overrides)[f.id],
              );
              return (
                <li key={f.id} className="flex-shrink-0">
                  <button
                    type="button"
                    onClick={() => setSelectedId(f.id)}
                    className={`w-full text-left whitespace-nowrap text-sm px-3 py-2 rounded-md transition-colors ${
                      active
                        ? "bg-slate-50 text-linkColor font-medium"
                        : "text-slate-600 hover:bg-slate-100"
                    }`}
                  >
                    {(overrides[f.id] && overrides[f.id].title) || f.title}
                    {changed && (
                      <span className="ml-1.5 inline-block w-1.5 h-1.5 rounded-full bg-themeColor align-middle" />
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>

        {/* Right: field editor */}
        <div className="flex-1 min-w-0">
          {/* Title */}
          <div className="mb-4">
            <label className="block text-xs font-semibold text-slate-600 mb-1">
              Form title
            </label>
            <input
              type="text"
              value={(ov.title != null ? ov.title : form.title) || ""}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
            />
          </div>

          {/* Fields */}
          <div className="space-y-2">
            {rows.map((row, i) => (
              <FieldRow
                key={row.key}
                row={row}
                first={i === 0}
                last={i === rows.length - 1}
                onMoveUp={() => moveRow(i, -1)}
                onMoveDown={() => moveRow(i, 1)}
                onLabel={(v) =>
                  row.base
                    ? patchBaseField(row.key, {
                        label: v === row.baseLabel ? undefined : v,
                      })
                    : patchAddedField(row.key, { label: v })
                }
                onToggleRequired={() =>
                  patchBaseField(row.key, { required: !row.required })
                }
                onToggleHidden={() =>
                  patchBaseField(row.key, {
                    hidden: row.hidden ? undefined : true,
                  })
                }
                onReset={() => resetBaseField(row.key)}
                onRemove={() => removeAddedField(row.key)}
                onAddOption={() => addOption(row)}
                onRenameOption={(idx, v) => renameOption(row, idx, v)}
                onRemoveOption={(idx) => removeOption(row, idx)}
              />
            ))}
          </div>

          {/* Add a field */}
          <div className="mt-5 border border-dashed border-slate-300 rounded-md p-3 bg-slate-50">
            <p className="text-xs font-semibold text-slate-600 mb-2">
              Add a new field
            </p>
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                type="text"
                placeholder="Field label"
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
                className="flex-1 border border-slate-300 rounded-md px-3 py-2 text-sm"
              />
              <select
                value={newType}
                onChange={(e) => {
                  setNewType(e.target.value);
                  if (isSelectType(e.target.value)) setNewOptions([""]);
                }}
                className="border border-slate-300 rounded-md px-3 py-2 text-sm bg-white"
              >
                {ADDABLE_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
              <button
                onClick={addField}
                className="inline-flex items-center gap-1 px-3 py-2 text-sm font-semibold rounded-md bg-slate-700 hover:bg-slate-800 text-white"
              >
                <Plus size={14} />
                Add
              </button>
            </div>
            {isSelectType(newType) && (
              <div className="mt-2 space-y-1.5">
                <p className="text-xs text-slate-500">Options</p>
                {newOptions.map((opt, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input
                      type="text"
                      value={opt}
                      placeholder={`Option ${i + 1}`}
                      onChange={(e) => {
                        const arr = newOptions.slice();
                        arr[i] = e.target.value;
                        setNewOptions(arr);
                      }}
                      className="flex-1 border border-slate-300 rounded-md px-2 py-1 text-sm"
                    />
                    <button
                      onClick={() => {
                        if (newOptions.length <= 1) return;
                        setNewOptions(newOptions.filter((_, x) => x !== i));
                      }}
                      disabled={newOptions.length <= 1}
                      className="text-slate-400 hover:text-red-500 disabled:opacity-30"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
                <button
                  onClick={() => setNewOptions([...newOptions, ""])}
                  className="text-xs text-linkColor hover:text-themeColor inline-flex items-center gap-1"
                >
                  <Plus size={12} /> Add option
                </button>
              </div>
            )}
          </div>

          {formHasOverride && (
            <p className="text-xs text-slate-400 mt-3">
              This form has customizations. Use “Reset to default” on a field to
              restore its original label, options, and visibility.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

// --- one field row ----------------------------------------------------------

function FieldRow({
  row,
  first,
  last,
  onMoveUp,
  onMoveDown,
  onLabel,
  onToggleRequired,
  onToggleHidden,
  onReset,
  onRemove,
  onAddOption,
  onRenameOption,
  onRemoveOption,
}) {
  const select = isSelectType(row.type);
  const options = Array.isArray(row.options) ? row.options : [];
  const editableLabel = row.type !== "video";

  return (
    <div
      className={`border rounded-md p-3 ${
        row.hidden
          ? "border-slate-200 bg-slate-50 opacity-70"
          : "border-slate-200 bg-white"
      }`}
    >
      <div className="flex items-start gap-2">
        {/* reorder */}
        <div className="flex flex-col pt-1">
          <button
            onClick={onMoveUp}
            disabled={first}
            className="text-slate-400 hover:text-slate-700 disabled:opacity-25"
            title="Move up"
          >
            <ChevronUp size={16} />
          </button>
          <button
            onClick={onMoveDown}
            disabled={last}
            className="text-slate-400 hover:text-slate-700 disabled:opacity-25"
            title="Move down"
          >
            <ChevronDown size={16} />
          </button>
        </div>

        {/* label + meta */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            {editableLabel ? (
              <input
                type="text"
                value={row.label || ""}
                onChange={(e) => onLabel(e.target.value)}
                className="flex-1 border border-slate-300 rounded-md px-2 py-1.5 text-sm"
              />
            ) : (
              <span className="flex-1 text-sm text-slate-700">{row.label}</span>
            )}
            <span className="text-[11px] uppercase tracking-wide text-slate-400 flex-shrink-0">
              {TYPE_LABELS[row.type] || row.type}
            </span>
            {!row.base && (
              <span className="text-[10px] uppercase tracking-wide text-emerald-600 bg-emerald-50 border border-emerald-200 rounded px-1 py-0.5 flex-shrink-0">
                Added
              </span>
            )}
          </div>

          {/* options */}
          {select && (
            <div className="mt-2 pl-1 space-y-1.5">
              {options.map((opt, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input
                    type="text"
                    value={opt}
                    onChange={(e) => onRenameOption(i, e.target.value)}
                    className="flex-1 border border-slate-200 rounded-md px-2 py-1 text-sm"
                  />
                  <button
                    onClick={() => onRemoveOption(i)}
                    disabled={options.length <= 1}
                    className="text-slate-400 hover:text-red-500 disabled:opacity-30"
                    title="Remove option"
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
              <button
                onClick={onAddOption}
                className="text-xs text-linkColor hover:text-themeColor inline-flex items-center gap-1"
              >
                <Plus size={12} /> Add option
              </button>
            </div>
          )}

          {/* controls */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-xs">
            {row.base && row.type !== "video" && (
              <label className="inline-flex items-center gap-1.5 text-slate-600 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!row.required}
                  onChange={onToggleRequired}
                  className="rounded border-slate-300"
                />
                Required
              </label>
            )}

            {row.base ? (
              <button
                onClick={onToggleHidden}
                className="inline-flex items-center gap-1 text-slate-500 hover:text-slate-800"
              >
                {row.hidden ? <Eye size={13} /> : <EyeOff size={13} />}
                {row.hidden ? "Show" : "Hide"}
              </button>
            ) : (
              <button
                onClick={onRemove}
                className="inline-flex items-center gap-1 text-slate-500 hover:text-red-600"
              >
                <Trash2 size={13} />
                Remove
              </button>
            )}

            {row.base && row.hasOverride && (
              <button
                onClick={onReset}
                className="inline-flex items-center gap-1 text-slate-500 hover:text-themeColor"
              >
                <RotateCcw size={13} />
                Reset to default
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
